import React, { useRef, useState } from 'react';
import { attachmentsApi } from '@/api/shopify.api';
import { formatBytes } from '@/lib/format';
import { ApiError } from '@/lib/errors';

export interface StagedFile {
  filename: string;
  s3_key: string;
  mime_type?: string | null;
  size_bytes: number;
}

interface Props {
  /** Files already staged & ready to be sent with the parent action. */
  value: StagedFile[];
  onChange: (next: StagedFile[]) => void;
  /** Optional max number of files. Default: 10. */
  maxFiles?: number;
  /** Optional label override. Default: "Attachments (optional)". */
  label?: string;
}

/**
 * Reusable upload widget for ticket action modals (Add note, Change status,
 * Log CX, Handoff, Reassign, Approve, Close, Archive). Drag-drop + browse,
 * multi-file, shows preview chips, lets the user remove a file before submit.
 *
 * Each file is uploaded immediately to /api/v1/attachments/upload (same flow
 * the New Ticket form uses) and the returned s3_key is stashed in the parent's
 * `value` array. The parent passes that array through to its action endpoint.
 */
export function FileUploadField({ value, onChange, maxFiles = 10, label = 'Attachments (optional)' }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (files: FileList | File[]) => {
    setError(null);
    const list = Array.from(files);
    const remaining = Math.max(0, maxFiles - value.length);
    if (list.length > remaining) {
      setError(`Maximum ${maxFiles} files. ${remaining} slot(s) remaining.`);
      return;
    }
    setUploading((n) => n + list.length);
    try {
      const uploaded: StagedFile[] = [];
      for (const file of list) {
        const res = await attachmentsApi.upload(file);
        uploaded.push({
          filename: res.filename,
          s3_key: res.s3_key,
          mime_type: res.mime_type,
          size_bytes: res.size_bytes
        });
      }
      onChange([...value, ...uploaded]);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.problem?.detail ?? err.message
          : 'Upload failed.'
      );
    } finally {
      setUploading((n) => Math.max(0, n - list.length));
    }
  };

  const onPick = () => inputRef.current?.click();

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      void handleFiles(e.target.files);
      // reset so picking the same file twice still triggers
      e.target.value = '';
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  const removeAt = (i: number) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div className="form-group" style={{ margin: 0 }}>
      <label className="form-label">{label}</label>
      <div
        className={`file-drop ${dragOver ? 'file-drop-active' : ''}`}
        onClick={onPick}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={onInputChange}
        />
        <div className="file-drop-text">
          {uploading > 0 ? (
            <span>Uploading {uploading} file(s)…</span>
          ) : (
            <>
              <strong>Click</strong> or drop files here · up to {maxFiles}
              <div className="text-small text-muted" style={{ marginTop: 4 }}>
                Images, PDFs, ZIP, Office docs, videos · max 1 GB each
              </div>
            </>
          )}
        </div>
      </div>
      {value.length > 0 ? (
        <ul className="file-chip-list">
          {value.map((f, i) => (
            <li key={`${f.s3_key}-${i}`} className="file-chip">
              <span className="file-chip-name" title={f.filename}>📎 {f.filename}</span>
              <span className="file-chip-size text-muted">{formatBytes(f.size_bytes)}</span>
              <button
                type="button"
                className="file-chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(i);
                }}
                aria-label={`Remove ${f.filename}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <div className="form-error">{error}</div> : null}
    </div>
  );
}
